/* Valor purchase history — "last ordered" notes on product cards (v3).
 *
 * Builds a per-dealer index of order lines (one row per document + SKU) for the
 * last 8 months by trickling the valor proxy's order-list / order-detail
 * actions (same auth and contract as assets/valor-account.js) into IndexedDB,
 * then appends a passive reorder note to the right side of every
 * .vpc-stock[data-vpc-sku] card stock line, opposite the inventory status
 * (below the Restock ETA when one is present — CSS in the snippet):
 *   - "3,960 units · 30 days" — total units bought in the last 30 days
 *   - "24 units · Mar 2026"   — nothing in 30 days: the most recent purchase
 * v2 showed only the most recent order's quantity, so a dealer who bought
 * ~4,000 last month and then 1 more read "1 unit" (Cameron 2026-09-24).
 *
 * A purchase is keyed by its sales order: invoices carry the SO number
 * (orderNo), open sales orders are their own key. Per purchase + SKU the
 * larger of ordered vs invoiced quantity counts, so an open SO and its later
 * invoice(s) are never added together.
 *
 * Boot modes (config from snippets/valor-purchase-history.liquid):
 *   guest    — no config: delete any stale DB left by an interrupted logout.
 *   non-B2B  — no proxy auth: reduce the legacy window.VALOR_ORDER_LINES dump
 *              in memory (v1 behavior, ~50-order ceiling) and render.
 *   B2B      — full engine: hydrate notes from IndexedDB immediately, then
 *              resume/continue the background sync (state machine persisted in
 *              the DB, so any page unload resumes at order granularity).
 *
 * Load control: one proxy request per second wrapped in requestIdleCallback,
 * paused while the tab is hidden, one syncing tab at a time via Web Locks,
 * 10s/30s/90s backoff on failures with a hard stop after 5 in a row.
 * Storage is purged on logout (shadow-DOM-aware click hook + next-load sweep)
 * and whenever the stored customer id doesn't match the current one.
 */
(function () {
  'use strict';
  if (window.__vphEngine) return;
  window.__vphEngine = true;

  // New name rather than a version bump: an upgrade would stall while any tab
  // still running the v2 script holds the old DB open.
  var DB_NAME = 'valor-vph3';
  var DB_VERSION = 1;
  var LEGACY_DB_NAMES = ['valor-vph']; // v2 (latest order per SKU) — deleted on boot
  var OWNER_KEY = 'vph:owner';
  var TICK_MS = 1000;
  var IDLE_TIMEOUT_MS = 2000;
  var BACKOFF_MS = [10000, 30000, 90000];
  var MAX_CONSECUTIVE_FAILURES = 5;
  var MAX_ITEM_ATTEMPTS = 3;
  var MAX_LIST_PAGES = 40;
  var TOPUP_PAGE_CAP = 5;
  /* ERP invoices carry date-only createdAt (midnight UTC), so a same-day
     invoice can predate the build watermark by up to ~24h + timezone. A 48h
     re-check window keeps them from being missed; reprocessing inside the
     window is idempotent (merge rule replaces same-order entries). */
  var TOPUP_SLOP_MS = 48 * 3600 * 1000;
  var MONTH_MS = 30.44 * 86400000;
  var RECENT_MS = 30 * 86400000; // "units in the last 30 days" window
  var LOCK_RETRY_MS = 60000;
  var META_SKU_RE = /SKU:\s*(\S+)/;

  var config = window.__vphConfig || null;
  var months = (config && config.months) || 8;

  /* ── storage helpers (all best-effort) ─────────────────────────────── */

  function lsGet(k) { try { return localStorage.getItem(k); } catch (_) { return null; } }
  function lsSet(k, v) { try { localStorage.setItem(k, v); } catch (_) {} }
  function lsRemove(k) { try { localStorage.removeItem(k); } catch (_) {} }
  function ssGet(k) { try { return sessionStorage.getItem(k); } catch (_) { return null; } }
  function ssSet(k, v) { try { sessionStorage.setItem(k, v); } catch (_) {} }
  function ssRemove(k) { try { sessionStorage.removeItem(k); } catch (_) {} }

  function topupKey() { return 'vph_topup_' + (config ? config.customerId : ''); }

  /* ── minimal promisified IndexedDB wrapper ─────────────────────────── */

  var dbPromise = null;
  var dbConnection = null;

  function openDb() {
    if (dbPromise) return dbPromise;
    dbPromise = new Promise(function (resolve, reject) {
      var req;
      try { req = indexedDB.open(DB_NAME, DB_VERSION); } catch (e) { reject(e); return; }
      req.onupgradeneeded = function () {
        var db = req.result;
        if (!db.objectStoreNames.contains('lines')) {
          var lines = db.createObjectStore('lines', { keyPath: 'id' });
          lines.createIndex('ts', 'ts');
        }
        if (!db.objectStoreNames.contains('queue')) {
          db.createObjectStore('queue', { keyPath: 'id' }).createIndex('ts', 'ts');
        }
        if (!db.objectStoreNames.contains('meta')) {
          db.createObjectStore('meta', { keyPath: 'key' });
        }
      };
      req.onsuccess = function () {
        dbConnection = req.result;
        // Let a newer version of this script (another tab) upgrade the DB.
        dbConnection.onversionchange = function () { dbConnection.close(); };
        resolve(req.result);
      };
      req.onerror = function () { reject(req.error); };
    });
    return dbPromise;
  }

  function idbRequest(storeName, mode, run) {
    return openDb().then(function (db) {
      return new Promise(function (resolve, reject) {
        var req = run(db.transaction(storeName, mode).objectStore(storeName));
        req.onsuccess = function () { resolve(req.result); };
        req.onerror = function () { reject(req.error); };
      });
    });
  }

  function idbGet(store, key) { return idbRequest(store, 'readonly', function (s) { return s.get(key); }); }
  function idbGetAll(store) { return idbRequest(store, 'readonly', function (s) { return s.getAll(); }); }
  function idbPut(store, value) { return idbRequest(store, 'readwrite', function (s) { return s.put(value); }); }
  function idbDelete(store, key) { return idbRequest(store, 'readwrite', function (s) { return s.delete(key); }); }
  function idbClear(store) { return idbRequest(store, 'readwrite', function (s) { return s.clear(); }); }

  function idbPutAll(storeName, values) {
    if (!values.length) return Promise.resolve();
    return openDb().then(function (db) {
      return new Promise(function (resolve, reject) {
        var t = db.transaction(storeName, 'readwrite');
        var s = t.objectStore(storeName);
        values.forEach(function (v) { s.put(v); });
        t.oncomplete = function () { resolve(); };
        t.onerror = function () { reject(t.error); };
        t.onabort = function () { reject(t.error); };
      });
    });
  }

  /* Newest order still waiting in the queue (ts index, descending). */
  function idbNewestQueueItem() {
    return openDb().then(function (db) {
      return new Promise(function (resolve, reject) {
        var req = db.transaction('queue', 'readonly').objectStore('queue').index('ts').openCursor(null, 'prev');
        req.onsuccess = function () { resolve(req.result ? req.result.value : null); };
        req.onerror = function () { reject(req.error); };
      });
    });
  }

  /* Fire-and-forget: a delete waits for other tabs' connections to close,
     and nothing here depends on it finishing. */
  function deleteLegacyDatabases() {
    LEGACY_DB_NAMES.forEach(function (name) {
      try { indexedDB.deleteDatabase(name); } catch (_) {}
    });
  }

  function deleteDatabase(onDone) {
    try { if (dbConnection) dbConnection.close(); } catch (_) {}
    dbConnection = null;
    dbPromise = null;
    deleteLegacyDatabases();
    try {
      var req = indexedDB.deleteDatabase(DB_NAME);
      req.onsuccess = function () { if (onDone) onDone(); };
    } catch (_) {}
  }

  /* ── renderer (ported from v1 snippets/valor-purchase-history.liquid) ── */

  var RECEIPT_SVG =
    '<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" ' +
    'stroke-width="2" stroke-linecap="round" stroke-linejoin="round">' +
    '<path d="M5 21V5a1 1 0 0 1 1-1h12a1 1 0 0 1 1 1v16l-3-2-2 2-2-2-2 2-2-2-3 2z"/>' +
    '<path d="M9 8h6M9 12h6"/></svg>';

  var rowsBySku = new Map(); // sku -> Map(row id -> {id, sku, key, source, ts(ms), qty})
  var rendererStarted = false;

  function addRow(row) {
    var m = rowsBySku.get(row.sku);
    if (!m) { m = new Map(); rowsBySku.set(row.sku, m); }
    m.set(row.id, row);
  }

  function removeRow(row) {
    var m = rowsBySku.get(row.sku);
    if (!m) return;
    m.delete(row.id);
    if (!m.size) rowsBySku.delete(row.sku);
  }

  /* Per-SKU summary, computed at render time so the 30-day window is always
     relative to now: {recentQty, lastTs, lastQty} or null. */
  function summarize(sku) {
    var m = rowsBySku.get(sku);
    if (!m) return null;
    var now = Date.now();
    var windowStart = now - months * MONTH_MS;
    var purchases = new Map(); // purchase key -> {ordered, invoiced, ts}
    m.forEach(function (r) {
      if (r.ts < windowStart) return;
      var p = purchases.get(r.key);
      if (!p) { p = { ordered: 0, invoiced: 0, ts: 0 }; purchases.set(r.key, p); }
      if (r.source === 'invoice') p.invoiced += r.qty; else p.ordered += r.qty;
      if (r.ts > p.ts) p.ts = r.ts;
    });
    if (!purchases.size) return null;
    var recentQty = 0, lastTs = 0, lastQty = 0;
    purchases.forEach(function (p) {
      var qty = Math.max(p.ordered, p.invoiced);
      if (p.ts >= now - RECENT_MS) recentQty += qty;
      if (p.ts > lastTs) { lastTs = p.ts; lastQty = qty; } else if (p.ts === lastTs) { lastQty += qty; }
    });
    return { recentQty: recentQty, lastTs: lastTs, lastQty: lastQty };
  }

  function unitLabel(n) { return n.toLocaleString('en-US') + (n === 1 ? ' unit' : ' units'); }

  function fmtMonthYear(ts) {
    return new Date(ts).toLocaleString('en-US', { month: 'short', year: 'numeric' });
  }

  function fmtDay(ts) {
    return new Date(ts).toLocaleString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
  }

  function setNoteText(note, sum) {
    if (!note.querySelector('.reorder-note__txt')) {
      note.innerHTML = RECEIPT_SVG + '<span class="reorder-note__txt"></span>';
    }
    var recent = sum.recentQty > 0;
    note.querySelector('.reorder-note__txt').textContent = recent
      ? unitLabel(sum.recentQty) + ' · 30 days'
      : unitLabel(sum.lastQty) + ' · ' + fmtMonthYear(sum.lastTs);
    note.setAttribute('title', recent
      ? 'Units you ordered in the last 30 days (last order ' + fmtDay(sum.lastTs) + ')'
      : 'Your most recent order of this item');
  }

  function applyNote(stockEl) {
    var sku = stockEl.getAttribute('data-vpc-sku');
    var sum = sku ? summarize(sku) : null;
    var note = stockEl.querySelector('.reorder-note');
    if (!sum) { if (note) note.remove(); return; } // never/too-long-ago ordered: no note

    var sig = sum.recentQty + '-' + sum.lastTs + '-' + sum.lastQty;
    if (note && note.getAttribute('data-vph-sig') === sig) return;
    if (!note) {
      note = document.createElement('span');
      note.className = 'reorder-note';
      note.setAttribute('data-last-ordered', '');
      stockEl.appendChild(note);
    }
    note.setAttribute('data-vph-sig', sig);
    setNoteText(note, sum);
  }

  function scan(root) {
    if (!rowsBySku.size) return;
    (root || document).querySelectorAll('.vpc-stock[data-vpc-sku]').forEach(applyNote);
  }

  function startRenderer() {
    if (rendererStarted) return;
    rendererStarted = true;

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', function () { scan(); });
    } else {
      scan();
    }

    document.addEventListener('vph:updated', function () { scan(); });

    // Re-decorate cards injected by collection filtering / pagination / morph.
    var pending = false;
    var observer = new MutationObserver(function (mutations) {
      if (pending) return;
      for (var i = 0; i < mutations.length; i++) {
        if (mutations[i].addedNodes && mutations[i].addedNodes.length) {
          pending = true;
          window.requestAnimationFrame(function () {
            pending = false;
            scan();
          });
          break;
        }
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  function notifyUpdated(skus) {
    try {
      document.dispatchEvent(new CustomEvent('vph:updated', { detail: { skus: skus || [] } }));
    } catch (_) { scan(); }
  }

  /* ── proxy client (contract shared with assets/valor-account.js) ────── */

  function proxyPost(body) {
    var auth = window.__vaAuth || {};
    var payload = {
      customer_id: auth.customer_id,
      ts: auth.ts,
      token: auth.token,
      shop: (window.Shopify && window.Shopify.shop) || (config && config.shop) || ''
    };
    for (var k in body) { if (Object.prototype.hasOwnProperty.call(body, k)) payload[k] = body[k]; }
    return fetch(auth.proxy || window.__vaProxy || '', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    }).then(function (r) {
      if (!r.ok) {
        var httpErr = new Error('HTTP ' + r.status);
        if (r.status === 401 || r.status === 403) httpErr.vphAuth = true;
        throw httpErr;
      }
      return r.json();
    }).then(function (d) {
      if (!d || !d.ok) {
        var err = new Error((d && d.error) || 'Proxy error');
        if (d && /auth|token|signature|hmac|expired|denied|forbidden/i.test(String(d.error || ''))) err.vphAuth = true;
        throw err;
      }
      return d;
    });
  }

  /* ── sync engine ───────────────────────────────────────────────────── */

  var syncState = null;      // in-memory copy of the meta 'state' record
  var stopped = false;       // hard stop for this page load
  var running = false;       // a loop (lock held) is active
  var loopResolve = null;    // resolving this releases the Web Lock
  var tickTimer = null;
  var lockRetryTimer = null;
  var consecutiveFailures = 0;
  var consecutiveDrops = 0;  // items dropped back-to-back — 2 in a row means systemic outage
  var itemAttempts = {};     // order id -> failed attempts this page load
  var topup = null;          // in-memory top-up cursor {page, newest, queued}

  function freshState() {
    var now = Date.now();
    return {
      key: 'state',
      customerId: String(config.customerId),
      phase: 'enumerating',
      page: 1,
      cutoffTs: now - months * MONTH_MS,
      candidateWatermarkTs: now,
      watermarkTs: 0,
      enumeratedCount: 0,
      processedCount: 0,
      skippedCount: 0,
      lastSyncAt: 0,
      lastTopUpAt: 0,
      createdAt: now
    };
  }

  function saveState() { return idbPut('meta', syncState); }

  function syncNeeded() {
    if (!syncState) return false;
    if (syncState.phase !== 'complete') return true;
    return !ssGet(topupKey());
  }

  /* Another tab may have advanced the sync while we waited for the lock —
     re-read the persisted state before running. */
  function reloadState() {
    return idbGet('meta', 'state').then(function (state) {
      if (state && String(state.customerId) === String(config.customerId)) syncState = state;
    }).catch(function () {});
  }

  function maybeStartSync() {
    if (stopped || running || document.hidden || !syncNeeded()) return;
    if (navigator.locks && navigator.locks.request) {
      running = true;
      navigator.locks.request('vph-sync', { ifAvailable: true }, function (lock) {
        if (!lock) { running = false; scheduleLockRetry(); return; }
        return reloadState().then(runLoop); // lock held until this promise settles
      }).catch(function () { running = false; });
    } else {
      running = true;
      runLoop();
    }
  }

  function scheduleLockRetry() {
    if (lockRetryTimer) return;
    lockRetryTimer = setTimeout(function () {
      lockRetryTimer = null;
      maybeStartSync();
    }, LOCK_RETRY_MS);
  }

  function runLoop() {
    return new Promise(function (resolve) {
      loopResolve = resolve;
      tickSoon(0);
    });
  }

  function endLoop() {
    clearTimeout(tickTimer);
    tickTimer = null;
    running = false;
    if (loopResolve) { loopResolve(); loopResolve = null; }
  }

  function tickSoon(delayMs) {
    clearTimeout(tickTimer);
    tickTimer = setTimeout(function () {
      if (window.requestIdleCallback) {
        window.requestIdleCallback(tick, { timeout: IDLE_TIMEOUT_MS });
      } else {
        tick();
      }
    }, delayMs);
  }

  function tick() {
    if (stopped || document.hidden) { endLoop(); return; }
    step().then(function (hasMoreWork) {
      consecutiveFailures = 0;
      if (hasMoreWork) tickSoon(TICK_MS);
      else endLoop();
    }, function (err) {
      consecutiveFailures++;
      if (err && err.vphAuth) { stopped = true; endLoop(); return; } // fresh token needs a navigation
      if (consecutiveFailures >= MAX_CONSECUTIVE_FAILURES) { stopped = true; endLoop(); return; }
      tickSoon(BACKOFF_MS[Math.min(consecutiveFailures - 1, BACKOFF_MS.length - 1)]);
    });
  }

  function step() {
    if (!syncState) return Promise.resolve(false);
    if (syncState.phase === 'enumerating') return stepEnumerate();
    if (syncState.phase === 'processing') return stepProcess();
    if (syncState.phase === 'complete') return stepTopUp();
    return Promise.resolve(false);
  }

  /* Queue row for an order-list entry. key = the sales order this document
     belongs to (invoices carry it as orderNo), so an open SO and its invoices
     resolve to the same purchase. */
  function queueRow(o, ts) {
    return {
      id: String(o.id),
      source: o._source || 'order',
      sellTo: o._sellToCustomerNo || '',
      key: String(o.orderNo || o.name || o.id),
      ts: ts
    };
  }

  /* Phase 1: page through order-list, queueing orders inside the 8-month
     window. Assumes newest-first but stops defensively when a whole page
     falls outside the window. */
  function stepEnumerate() {
    return proxyPost({ action: 'order-list', page: syncState.page }).then(function (data) {
      var orders = data.orders || [];
      var anyInWindow = false;
      var rows = [];
      orders.forEach(function (o) {
        var ts = Date.parse(o.createdAt || '');
        if (!isFinite(ts)) return;
        if (syncState.page === 1 && ts > syncState.candidateWatermarkTs) syncState.candidateWatermarkTs = ts;
        if (ts < syncState.cutoffTs) return;
        anyInWindow = true;
        if (o.lineItemsCount === 0 || o.cancelledAt) return;
        rows.push(queueRow(o, ts));
      });
      return idbPutAll('queue', rows).then(function () {
        syncState.enumeratedCount += rows.length;
        if (!data.hasMore || !anyInWindow || syncState.page >= MAX_LIST_PAGES) {
          syncState.phase = 'processing';
        } else {
          syncState.page += 1;
        }
        return saveState().then(function () { return true; });
      });
    });
  }

  /* Phase 2: newest-first, one order-detail per tick, written to 'lines'. */
  function stepProcess() {
    return idbNewestQueueItem().then(function (item) {
      if (!item) return finishBuild().then(function () { return false; });
      return proxyPost({
        action: 'order-detail',
        order_id: item.id,
        source: item.source || '',
        sell_to_customer_no: item.sellTo || ''
      }).then(function (data) {
        var order = data.data || {};
        var orderTs = Date.parse(order.createdAt || '');
        if (!isFinite(orderTs)) orderTs = item.ts;
        var bySku = aggregateLines(order.lineItems && order.lineItems.nodes);
        return mergeOrderLines(item, orderTs, bySku);
      }).then(function (changed) {
        consecutiveDrops = 0;
        changed.forEach(addRow);
        return idbDelete('queue', item.id).then(function () {
          syncState.processedCount += 1;
          return saveState();
        }).then(function () {
          if (changed.length) notifyUpdated(changed.map(function (e) { return e.sku; }));
          return true;
        });
      }).catch(function (err) {
        if (err && err.vphAuth) throw err;
        itemAttempts[item.id] = (itemAttempts[item.id] || 0) + 1;
        if (itemAttempts[item.id] < MAX_ITEM_ATTEMPTS) throw err; // backoff, retry same item
        // A poison order (persistently failing while others succeed) is
        // dropped so the build never stalls; a second drop in a row means the
        // proxy itself is down — keep the row and let the hard stop preserve
        // the queue for the next page load.
        consecutiveDrops += 1;
        if (consecutiveDrops >= 2) throw err;
        return idbDelete('queue', item.id).then(function () {
          syncState.skippedCount += 1;
          return saveState();
        }).then(function () { return true; });
      });
    });
  }

  /* Per-session incremental top-up once a build is complete: re-check
     order-list page 1 for orders newer than the watermark (minus the 48h
     invoice-date slop), queue them, and fall back into 'processing'. */
  function stepTopUp() {
    if (ssGet(topupKey())) return Promise.resolve(false);
    if (!topup) topup = { page: 1, newest: syncState.watermarkTs || 0, queued: 0 };
    return proxyPost({ action: 'order-list', page: topup.page }).then(function (data) {
      var orders = data.orders || [];
      var threshold = (syncState.watermarkTs || 0) - TOPUP_SLOP_MS;
      var allNewer = orders.length > 0;
      var rows = [];
      orders.forEach(function (o) {
        var ts = Date.parse(o.createdAt || '');
        if (!isFinite(ts)) return;
        if (ts > topup.newest) topup.newest = ts;
        if (ts <= threshold) { allNewer = false; return; }
        if (o.lineItemsCount === 0 || o.cancelledAt) return;
        rows.push(queueRow(o, ts));
      });
      return idbPutAll('queue', rows).then(function () {
        topup.queued += rows.length;
        if (allNewer && data.hasMore && topup.page < TOPUP_PAGE_CAP) {
          topup.page += 1;
          return true;
        }
        ssSet(topupKey(), '1'); // checked this session
        if (topup.queued > 0) {
          syncState.phase = 'processing';
          syncState.candidateWatermarkTs = Math.max(syncState.candidateWatermarkTs || 0, topup.newest, Date.now());
          return saveState().then(function () { return true; });
        }
        syncState.watermarkTs = Math.max(syncState.watermarkTs || 0, topup.newest);
        syncState.lastTopUpAt = Date.now();
        return saveState().then(pruneOld).then(function () { return false; });
      });
    });
  }

  function finishBuild() {
    syncState.phase = 'complete';
    syncState.watermarkTs = Math.max(syncState.watermarkTs || 0, syncState.candidateWatermarkTs || 0);
    syncState.lastSyncAt = Date.now();
    ssSet(topupKey(), '1'); // just synced — no separate top-up needed this session
    return saveState().then(pruneOld);
  }

  /* Resolve each line's SKU (top-level for Shopify orders; parsed out of the
     "UPC: … · SKU: …" meta string for ERP invoices) and sum split lines. */
  function aggregateLines(nodes) {
    var bySku = new Map();
    (nodes || []).forEach(function (li) {
      var sku = li.sku;
      if (!sku && li.meta) {
        var m = META_SKU_RE.exec(li.meta);
        if (m) sku = m[1];
      }
      if (!sku) return;
      bySku.set(sku, (bySku.get(sku) || 0) + (li.quantity || 0));
    });
    return bySku;
  }

  /* One row per document + SKU, written atomically per document. Re-processing
     a document (top-up slop window) overwrites the same rows. */
  function mergeOrderLines(item, orderTs, bySku) {
    if (!bySku.size) return Promise.resolve([]);
    var rows = [];
    bySku.forEach(function (qty, sku) {
      rows.push({
        id: item.id + '|' + sku,
        sku: sku,
        key: item.key || item.id,
        source: item.source || 'order',
        ts: orderTs,
        qty: qty
      });
    });
    return idbPutAll('lines', rows).then(function () { return rows; });
  }

  /* Drop entries older than the rolling window (render filters too — this
     just keeps the store from growing stale). */
  function pruneOld() {
    var cutoff = Date.now() - months * MONTH_MS;
    return openDb().then(function (db) {
      return new Promise(function (resolve) {
        var t = db.transaction('lines', 'readwrite');
        var req = t.objectStore('lines').index('ts').openCursor(window.IDBKeyRange.upperBound(cutoff));
        req.onsuccess = function () {
          var cur = req.result;
          if (!cur) return;
          removeRow(cur.value);
          cur.delete();
          cur.continue();
        };
        t.oncomplete = function () { resolve(); };
        t.onerror = function () { resolve(); };
      });
    }).catch(function () {});
  }

  /* ── lifecycle: logout purge, guest sweep, identity check ──────────── */

  function purgeOnLogout() {
    stopped = true;
    endLoop();
    ssRemove(topupKey());
    // Best-effort before navigation; the marker survives an interrupted
    // delete, so the guest sweep finishes the job on the next page load.
    deleteDatabase(function () { lsRemove(OWNER_KEY); });
  }

  document.addEventListener('click', function (e) {
    var anchor = null;
    var path = e.composedPath ? e.composedPath() : [];
    for (var i = 0; i < path.length; i++) {
      var n = path[i];
      if (n && n.tagName === 'A' && typeof n.href === 'string' && n.href.indexOf('/account/logout') !== -1) {
        anchor = n;
        break;
      }
    }
    if (!anchor && e.target && e.target.closest) anchor = e.target.closest('a[href*="/account/logout"]');
    if (anchor) purgeOnLogout();
  }, true);

  function sweepStaleDb() {
    if (!lsGet(OWNER_KEY)) return;
    deleteDatabase(function () { lsRemove(OWNER_KEY); });
  }

  /* ── boot ──────────────────────────────────────────────────────────── */

  function bootLegacy() {
    // Non-B2B: no proxy auth — reduce the Liquid dump in memory (v1 behavior).
    sweepStaleDb(); // any DB on disk belongs to a (different) B2B login
    var lines = window.VALOR_ORDER_LINES || [];
    lines.forEach(function (l, i) {
      if (!l || !l.sku) return;
      var ts = (l.ts || 0) * 1000;
      // Shopify orders only — one order per timestamp, no invoices to dedupe.
      addRow({ id: 'l' + i, sku: l.sku, key: 'o' + ts, source: 'order', ts: ts, qty: l.qty || 0 });
    });
    startRenderer();
  }

  function bootB2B() {
    idbGet('meta', 'state').then(function (state) {
      if (!state || String(state.customerId) !== String(config.customerId)) {
        // First build, or another customer's data on this browser: start clean.
        return Promise.all([idbClear('lines'), idbClear('queue'), idbClear('meta')]).then(function () {
          syncState = freshState();
          return saveState();
        });
      }
      syncState = state;
    }).then(function () {
      lsSet(OWNER_KEY, String(config.customerId));
      return idbGetAll('lines');
    }).then(function (rows) {
      (rows || []).forEach(addRow);
      startRenderer();
      // No sync in the theme editor, and none without proxy auth (renderer
      // still serves whatever the DB already holds).
      if (!config.designMode && window.__vaAuth) {
        maybeStartSync();
        document.addEventListener('visibilitychange', function () {
          if (!document.hidden) maybeStartSync();
        });
      }
    }).catch(function (err) {
      // IndexedDB unavailable (private mode etc.) — accepted empty state.
      if (window.console && console.warn) console.warn('[vph] disabled:', err && err.message);
    });
  }

  deleteLegacyDatabases();

  if (!config || !window.__vrIsCustomer) {
    sweepStaleDb();
  } else if (config.b2b !== true) {
    bootLegacy(); // mirrors the snippet's `unless b2b?` dump gate
  } else {
    bootB2B();
  }

  /* Read API for the account page's "Wishlist & quick reorder" tab
     (assets/valor-reorder.js): one row per SKU bought in the last `monthsBack`
     months, deduped per sales order the same way the card notes are. */
  window.vaPurchaseHistory = {
    purchases: function (monthsBack) {
      var now = Date.now();
      var windowStart = now - (monthsBack || months) * MONTH_MS;
      var out = [];
      rowsBySku.forEach(function (m, sku) {
        var purchases = new Map();
        m.forEach(function (r) {
          if (r.ts < windowStart) return;
          var p = purchases.get(r.key);
          if (!p) { p = { ordered: 0, invoiced: 0, ts: 0 }; purchases.set(r.key, p); }
          if (r.source === 'invoice') p.invoiced += r.qty; else p.ordered += r.qty;
          if (r.ts > p.ts) p.ts = r.ts;
        });
        if (!purchases.size) return;
        var units = 0, lastTs = 0, lastQty = 0;
        purchases.forEach(function (p) {
          var qty = Math.max(p.ordered, p.invoiced);
          units += qty;
          if (p.ts > lastTs) { lastTs = p.ts; lastQty = qty; } else if (p.ts === lastTs) { lastQty += qty; }
        });
        out.push({ sku: sku, orders: purchases.size, units: units, lastTs: lastTs, lastQty: lastQty });
      });
      return out;
    },
    get syncing() { return !!(syncState && syncState.phase !== 'complete' && !stopped); }
  };

  /* Console access for QA (verification checklist). */
  window.__vphDebug = {
    get state() { return syncState; },
    rows: rowsBySku,
    summarize: summarize,
    scan: scan
  };
})();
